import { getAnalyticsEvents } from './analyticsService';
import { format } from 'date-fns';

export interface NotificationMetrics {
  notificationType: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
}

export interface CampaignMetrics {
  campaignType: string;
  engagementRate: number;
  conversionRate: number;
}

export interface NotificationAnalytics {
  notificationMetrics: NotificationMetrics[];
  campaignMetrics: CampaignMetrics[];
  totalSent: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  bestSendTimes: Array<{ hour: number; engagementRate: number }>;
}

export const getNotificationAnalytics = async (
  days: number = 30
): Promise<NotificationAnalytics> => {
  try {
    const events = await getAnalyticsEvents(days);

    const notificationEvents = events.filter((e) =>
      e.eventName === 'notification_received' ||
      e.eventName === 'notification_opened' ||
      e.eventName === 'notification_dismissed' ||
      e.eventName === 'notification_tapped' ||
      e.category === 'notifications'
    );

    const receivedEvents = notificationEvents.filter(
      (e) => e.eventName === 'notification_received'
    );
    const openedEvents = notificationEvents.filter(
      (e) => e.eventName === 'notification_opened' || e.eventName === 'notification_tapped'
    );

    const notificationTypeMap = new Map<string, {
      sent: number;
      delivered: number;
      opened: number;
      clicked: number;
    }>();

    receivedEvents.forEach((event) => {
      const notificationType =
        (event.properties.notification_type as string) || 'unknown';

      if (!notificationTypeMap.has(notificationType)) {
        notificationTypeMap.set(notificationType, { sent: 0, delivered: 0, opened: 0, clicked: 0 });
      }
      const metrics = notificationTypeMap.get(notificationType)!;
      metrics.sent++;
      metrics.delivered++;
    });

    openedEvents.forEach((event) => {
      const notificationType =
        (event.properties.notification_type as string) || 'unknown';

      if (!notificationTypeMap.has(notificationType)) {
        notificationTypeMap.set(notificationType, { sent: 0, delivered: 0, opened: 0, clicked: 0 });
      }
      notificationTypeMap.get(notificationType)!.opened++;
    });

    const notificationMetrics: NotificationMetrics[] = Array.from(notificationTypeMap.entries())
      .map(([notificationType, metrics]) => ({
        notificationType,
        ...metrics,
      }));

    const campaignMetrics: CampaignMetrics[] = notificationMetrics.map((metric) => ({
      campaignType: metric.notificationType,
      engagementRate: metric.delivered > 0 ? (metric.opened / metric.delivered) * 100 : 0,
      conversionRate: metric.delivered > 0 ? (metric.clicked / metric.delivered) * 100 : 0,
    }));

    const totalSent = notificationMetrics.reduce((sum, m) => sum + m.sent, 0);
    const totalDelivered = notificationMetrics.reduce((sum, m) => sum + m.delivered, 0);
    const totalOpened = notificationMetrics.reduce((sum, m) => sum + m.opened, 0);
    const totalClicked = notificationMetrics.reduce((sum, m) => sum + m.clicked, 0);

    const hourCounts = new Map<number, { total: number; opened: number }>();
    receivedEvents.forEach((event) => {
      const hour = event.timestamp.getHours();
      if (!hourCounts.has(hour)) hourCounts.set(hour, { total: 0, opened: 0 });
      hourCounts.get(hour)!.total++;
    });
    openedEvents.forEach((event) => {
      const hour = event.timestamp.getHours();
      if (!hourCounts.has(hour)) hourCounts.set(hour, { total: 0, opened: 0 });
      hourCounts.get(hour)!.opened++;
    });

    const bestSendTimes = Array.from(hourCounts.entries())
      .map(([hour, data]) => ({
        hour,
        engagementRate: data.total > 0 ? (data.opened / data.total) * 100 : 0,
      }))
      .sort((a, b) => b.engagementRate - a.engagementRate);

    return {
      notificationMetrics,
      campaignMetrics,
      totalSent,
      deliveryRate: totalSent > 0 ? (totalDelivered / totalSent) * 100 : 0,
      openRate: totalDelivered > 0 ? (totalOpened / totalDelivered) * 100 : 0,
      clickRate: totalDelivered > 0 ? (totalClicked / totalDelivered) * 100 : 0,
      bestSendTimes,
    };
  } catch (error) {
    console.error('Error calculating notification analytics:', error);
    throw error;
  }
};
